import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import { useFavorites } from '@/hooks/useFavorites';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { formatCount, formatDuration, formatRating } from '@/utils/format';
import { getMovieById, getRelatedMovies } from '@/utils/movieQuery';

export default function Detail() {
  const { id = '' } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [showShare, setShowShare] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useDocumentTitle({
    title: movie ? `${movie.title} - 午夜小电影` : '影片未找到 - 午夜小电影',
    description: movie
      ? `${movie.title}（${movie.year}）是一部${movie.category}类午夜影片，时长 ${movie.duration} 分钟。${movie.midnightReason}`
      : '影片未找到，可能已下架。',
    keywords: movie ? `${movie.title},${movie.category}电影,午夜电影,午夜小电影,${movie.tags.join(',')}` : '午夜小电影',
  });

  if (!movie) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-28 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
          <i className="ri-film-line text-3xl"></i>
        </span>
        <h1 className="mt-6 text-xl text-foreground-950">影片未找到，可能已下架</h1>
        <p className="mt-2 text-sm text-foreground-600">你要找的影片不存在或链接已失效，去首页看看其他午夜好片吧。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-white transition hover:bg-primary-600"
          >
            <i className="ri-home-4-line"></i>返回首页
          </Link>
          <Link
            to="/search"
            className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-6 text-sm text-foreground-700 transition hover:text-foreground-950"
          >
            去搜索其他影片
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const related = getRelatedMovies(movie, 4);

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('链接已复制到剪贴板');
    } catch {
      setToast('复制失败，请手动复制地址栏链接');
    }
  };

  return (
    <div className="relative">
      <div className="absolute inset-x-0 top-0 h-[420px] overflow-hidden md:h-[520px]">
        <img
          src={movie.backdrop || movie.poster}
          alt={`${movie.title} 剧照`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/85 to-background-50" />
      </div>

      <div className="relative mx-auto w-full px-4 pt-6 md:px-8 md:pt-8">
        <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="transition hover:text-primary-400">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${encodeURIComponent(movie.category)}`} className="transition hover:text-primary-400">
            {movie.category}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{movie.title}</span>
        </nav>

        <div className="mt-8 flex flex-col gap-8 lg:flex-row lg:gap-12">
          <div className="w-full max-w-[260px] shrink-0 self-center lg:self-start">
            <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-300/50 bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} 午夜小电影`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="w-full flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-white">{movie.category}</span>
              {movie.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100/80 px-3 py-1 text-xs text-secondary-900">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-display text-4xl leading-tight text-foreground-950 md:text-5xl">{movie.title}</h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-500"></i>
                <span className="font-display text-lg text-foreground-950">{formatRating(movie.rating)}</span> 分
              </span>
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{movie.year} 年</span>
              <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>{formatDuration(movie.duration)}</span>
              <span className="flex items-center gap-1.5"><i className="ri-eye-line"></i>{formatCount(movie.views)} 次观看</span>
              <span className="flex items-center gap-1.5"><i className="ri-heart-3-line"></i>{formatCount(movie.favorites)} 人收藏</span>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => toggleFavorite(movie.id)}
                className={`flex h-11 items-center gap-2 whitespace-nowrap rounded-md px-6 text-sm font-medium transition ${
                  favorited
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'border border-background-300/70 text-foreground-700 hover:border-primary-400 hover:text-foreground-950'
                }`}
              >
                <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                {favorited ? '已收藏' : '收藏影片'}
              </button>
              <button
                type="button"
                onClick={() => setShowShare(true)}
                className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-6 text-sm text-foreground-700 transition hover:border-primary-400 hover:text-foreground-950"
              >
                <i className="ri-share-forward-line"></i>分享
              </button>
              <Link
                to={`/category/${encodeURIComponent(movie.category)}`}
                className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-6 text-sm text-foreground-700 transition hover:border-primary-400 hover:text-foreground-950"
              >
                <i className="ri-list-check-2"></i>更多{movie.category}影片
              </Link>
            </div>

            <div className="mt-6 flex items-start gap-3 rounded-lg border border-accent-500/30 bg-accent-500/10 p-4">
              <i className="ri-information-line mt-0.5 text-lg text-accent-500"></i>
              <p className="text-sm leading-relaxed text-foreground-700">
                本站仅提供影片介绍与观看前参考，<strong className="text-foreground-950">不提供在线播放与下载</strong>。
                如未来接入正规播放渠道，将第一时间在此更新。
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 text-xl text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>剧情简介
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">{movie.synopsis}</p>

            <h2 className="mt-10 flex items-center gap-2 text-xl text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-accent-500"></span>适合午夜观看的理由
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">{movie.midnightReason}</p>

            <h4 className="mt-10 text-base text-foreground-950">
              <a href="#related" className="transition hover:text-primary-400">相关推荐</a>
            </h4>
            <p className="mt-3 text-sm text-foreground-600">根据类型与标签为你匹配的相似午夜影片。</p>
          </div>

          <aside className="rounded-lg border border-background-300/50 bg-background-100 p-6">
            <h3 className="text-base text-foreground-950">影片信息</h3>
            <dl className="mt-4 flex flex-col divide-y divide-background-300/40 text-sm">
              <div className="flex justify-between gap-4 py-3">
                <dt className="text-foreground-500">导演</dt>
                <dd className="text-right text-foreground-800">{movie.director}</dd>
              </div>
              <div className="flex justify-between gap-4 py-3">
                <dt className="shrink-0 text-foreground-500">主演</dt>
                <dd className="text-right text-foreground-800">{movie.cast.join(' / ')}</dd>
              </div>
              <div className="flex justify-between gap-4 py-3">
                <dt className="text-foreground-500">类型</dt>
                <dd className="text-right text-foreground-800">{movie.category}</dd>
              </div>
              <div className="flex justify-between gap-4 py-3">
                <dt className="text-foreground-500">上映年份</dt>
                <dd className="text-right text-foreground-800">{movie.year}</dd>
              </div>
              <div className="flex justify-between gap-4 py-3">
                <dt className="text-foreground-500">影片时长</dt>
                <dd className="text-right text-foreground-800">{formatDuration(movie.duration)}</dd>
              </div>
            </dl>
            <p className="mt-4 text-xs leading-relaxed text-foreground-500">
              * 导演与主演信息为站内演示示例，仅供页面展示使用。
            </p>
          </aside>
        </div>

        <section id="related" className="mt-14">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl text-foreground-950">相关推荐</h2>
            <Link
              to={`/category/${encodeURIComponent(movie.category)}`}
              className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition hover:text-primary-400"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </div>

      {showShare && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background-950/70 px-4 backdrop-blur-sm"
          onClick={() => setShowShare(false)}
        >
          <div
            className="w-full max-w-md rounded-lg border border-background-300/60 bg-background-100 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg text-foreground-950">分享《{movie.title}》</h3>
                <p className="mt-1 text-sm text-foreground-600">复制下方链接，分享给同样喜欢午夜影片的朋友。</p>
              </div>
              <button
                type="button"
                onClick={() => setShowShare(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition hover:text-foreground-950"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="mt-5 flex items-center gap-2 rounded-md border border-background-300/60 bg-background-50 p-2 pl-4">
              <span className="flex-1 truncate text-sm text-foreground-600">{window.location.href}</span>
              <button
                type="button"
                onClick={copyLink}
                className="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-xs font-medium text-white transition hover:bg-primary-600"
              >
                <i className="ri-file-copy-line"></i>复制链接
              </button>
            </div>

            <div className="mt-5 flex gap-3">
              {[
                { icon: 'ri-weibo-line', label: '分享到微博' },
                { icon: 'ri-wechat-line', label: '分享到微信' },
                { icon: 'ri-link', label: '复制链接' },
              ].map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={copyLink}
                  className="flex flex-1 flex-col items-center gap-2 rounded-md border border-background-300/60 py-3 text-xs text-foreground-600 transition hover:border-primary-400 hover:text-foreground-950"
                >
                  <i className={`${s.icon} text-xl`}></i>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 rounded-full bg-background-100 px-5 py-3 text-sm text-foreground-900 shadow-none ring-1 ring-background-300/60">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-primary-400"></i>
            {toast}
          </span>
        </div>
      )}
    </div>
  );
}